import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor, JSONContent } from '@tiptap/react';
import type { RichEditorImageUploadHandler } from '../../rich-editor/types';
import { uploadImages, type UploadedImage } from '../../rich-editor/parts/editorHelpers';
import {
    hasImageSegment,
    importPastedImages,
    splitHtmlByImages
} from '../../rich-editor/parts/pastedImages';
import { inlineImageNode } from '../utils/inlineImages';

export interface InlineImageUpload {
    enabled: boolean;
    isUploading: boolean;
    error: string | null;
    dismissError: () => void;
    insertFiles: (editor: Editor, files: File[]) => Promise<void>;
    /** False when the HTML embeds no images, leaving the paste to the editor. */
    importHtml: (editor: Editor, html: string, insertAt: { from: number; to: number }) => boolean;
}

function toImageLine(images: UploadedImage[]): JSONContent[] {
    return [{ type: 'paragraph', content: images.map(inlineImageNode) }];
}

export function useInlineImageUpload(
    onImageUpload?: RichEditorImageUploadHandler
): InlineImageUpload {
    // A count, not a flag: overlapping batches would otherwise re-enable Send when the first one lands.
    const [uploadCount, setUploadCount] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const handlerRef = useRef(onImageUpload);
    useEffect(() => {
        handlerRef.current = onImageUpload;
    }, [onImageUpload]);

    const trackBusy = useCallback((busy: boolean) => {
        setUploadCount((count) => count + (busy ? 1 : -1));
    }, []);

    const upload = useCallback((file: File) => {
        const handler = handlerRef.current;
        return handler ? handler(file) : Promise.reject(new Error('Inline images are disabled.'));
    }, []);

    const insertFiles = useCallback(
        async (editor: Editor, files: File[]) => {
            setError(null);
            const images = await uploadImages(files, upload, setError, trackBusy);
            if (images.length === 0 || editor.isDestroyed) return;
            editor.chain().focus().insertContent(images.map(inlineImageNode)).run();
        },
        [upload, trackBusy]
    );

    const importHtml = useCallback(
        (editor: Editor, html: string, insertAt: { from: number; to: number }) => {
            const segments = splitHtmlByImages(html);
            if (!hasImageSegment(segments)) return false;
            setError(null);
            void importPastedImages(
                editor,
                segments,
                insertAt,
                upload,
                trackBusy,
                setError,
                toImageLine
            );
            return true;
        },
        [upload, trackBusy]
    );

    const dismissError = useCallback(() => setError(null), []);

    return {
        enabled: !!onImageUpload,
        isUploading: uploadCount > 0,
        error,
        dismissError,
        insertFiles,
        importHtml
    };
}
